<template>
  <div class="hero-section">
    <div class="content-container">
      <h1 class="hero-title">{{ title }}</h1>
      <h3 v-if="showTitleMessage" class="message">{{ titleMessage }}</h3>
      <p class="hero-subtitle" v-html="subtitle"></p>
      <p class="hero-subtitle-mob" >{{ heroSubtitleMob }}</p>
      <div v-if="showBannerImage && validBannerImage" class="banner-image-container">
        <img :src="bannerImageSrc" class="banner-image" />
      </div>
      <div v-if="showBannerImage && validBannerImage" class="banner-image-container-mobile">
        <img :src="mobileBannerImageSrc" class="banner-image-mobile" />
      </div>
      <div class="search-container" >
        <v-text-field
          ref="searchInput"
          v-model="internalQuery"
          :label="searchLabel"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          clearable
          class="search-input"
          id="search-input"
        ></v-text-field>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import debounce from 'lodash.debounce';

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: 'Blogs and Insights',
  },
  subtitle: {
    type: String,
    default:
      "Lorem ipsum is simply dummy text of the printing and typesetting industry.<br />Lorem ipsum has been the industry's standard dummy text ever.",
  },
  searchLabel: {
    type: String,
    default: 'Search Blogs and Insights',
  },
  backgroundImage: {
    type: String,
    default: '',
  },
  showBannerImage: {
    type: Boolean,
    default: false,
  },
  bannerImageMobile:{
    type: String,
    default: '',
  },
  titleMessage: {
    type: String,
    default: '',
  },
  showTitleMessage:{
    type: Boolean,
    default: false,
  },
  bannerImage: {
    type: String,
    default: '',
  },
  heroSubtitleMob: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:searchQuery']);

const internalQuery = ref(props.searchQuery);
const searchInput = ref(null);

// Validate bannerImage and compute the image source
const validBannerImage = computed(() => {
  return props.bannerImage || props.bannerImageMobile && typeof props.bannerImage || typeof props.bannerImageMobile === 'string' && props.bannerImage.trim() || props.bannerImageMobile.trim() !== '';
});

const bannerImageSrc = computed(() => {
  try {
    return validBannerImage.value ? require(`@/assets/img/${props.bannerImage}`) : '';
  } catch (error) {
    console.error(`Failed to load image: @/assets/img/${props.bannerImage}`, error);
    return '';
  }
});
const mobileBannerImageSrc = computed(() => {
  try {
    return validBannerImage.value ? require(`@/assets/img/${props.bannerImageMobile}`) : '';
  } catch (error) {
    console.error(`Failed to load image: @/assets/img/${props.bannerImageMobile}`, error);
    return '';
  }
});

// Debounce the search query update
const debouncedUpdate = debounce((val) => {
  emit('update:searchQuery', val);
}, 500);

// Watch local input changes
watch(internalQuery, (newVal) => {
  debouncedUpdate(newVal);
});

// Watch parent changes
watch(
  () => props.searchQuery,
  (newVal) => {
    internalQuery.value = newVal;
  }
);
document.getElementById('loaderSection').classList.add('d-none');
</script>

<style scoped>
.hero-section {
  background-color: v-bind('props.backgroundImage ? "transparent" : "#f5f5f5"');
  text-align: center;
  margin: 0 auto;
  padding: 40px 40px;
  width: 100%;
  background-image: v-bind(
    'props.backgroundImage ? `linear-gradient(to bottom, #f5f5f5 50%, transparent 50%), url(${props.backgroundImage})` : "none"'
  );
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 200px; /* Minimum height to ensure content is visible */
}

.banner-image-container {
  flex: v-bind('props.showBannerImage && validBannerImage ? "0" : "0"');
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
}

.banner-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.banner-image-container-mobile {
  flex: v-bind('props.showBannerImage && validBannerImage ? "0" : "0"');
  display: none;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
}

.banner-image-mobile {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.content-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.hero-title {
  font-size: 2.5em;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
}
.message{
  margin-bottom: 20px;
  font-weight: bold;
  font-size: 1.3rem;
}
.hero-subtitle {
  font-size: 1.1em;
  color: #666;
  line-height: 1.5;
  margin-bottom: 30px;
}
.hero-subtitle-mob{
  display: none;
}
.search-container {
  display: flex;
  justify-content: center;
}

.search-input {
  max-width: 400px;
}
@media screen and (max-width: 865px){
  .hero-subtitle-mob{
    display: block;
    font-size: 1.1em;
    color: #666;
    line-height: 1.5;
    margin-bottom: 30px;
  }
  .hero-subtitle{
    display: none;
  }
  
}
@media screen and (max-width: 790px){
  .banner-image-container {
    flex: v-bind('props.showBannerImage && validBannerImage ? "0" : "0"');
    display: none;
    justify-content: center;
    align-items: center;
    margin-bottom: 2rem;
  }
  .banner-image-container-mobile {
    flex: v-bind('props.showBannerImage && validBannerImage ? "0" : "0"');
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 2rem;
  }
}
</style>